import React, { useEffect } from "react";
import styles from "./Login.module.scss";
import { AuthService } from "../../components/AuthProvider/authService";
import { AuthDispatchContext } from "../../components/AuthProvider";
import fetchUser from "../../components/AuthProvider/fetchUser";

export const SET_CREATE_SESSION_FLAG = "SET_CREATE_SESSION_FLAG";
export const SET_ERROR = "SET_ERROR";
export const SET_REDIRECT_ROUTE = "SET_REDIRECT_ROUTE";
export const SET_SELECTED_CLIENT = "SET_SELECTED_CLIENT";
export const SET_USER = "SET_USER";

const Login = () => {
  useEffect(() => {
    const checkAuth = async () => {
      const userAuth = window.localStorage.getItem(
        "amplify-signin-with-hostedUI",
      );

      // ✅ If user is already authenticated, don't show loader
      if (userAuth) {
        // User has valid session, show login page normally
        return;
      }

      // ✅ Check if user is returning from OAuth2 authentication
      const urlParams = new URLSearchParams(window.location.search);
      const hasAuthParams =
        urlParams.get("code") ||
        urlParams.get("state") ||
        urlParams.get("session_state");

      // ✅ Check if current URL contains login-redirect
      const isLoginRedirect =
        window.location.pathname.includes("login-redirect") ||
        window.location.pathname.includes("oauth2");

      // FIX: Wrap refreshToken() in try/catch so that if the refresh token
      // is expired or invalid (e.g. user returns after hours), the error does
      // not prevent localStorage.clear() from running below. Without this,
      // stale USER_AUTH data persists across login attempts, causing repeated
      // 401 failures because the fetch interceptor keeps reading the old
      // expired idToken from localStorage.
      try {
        await AuthService.refreshToken();
      } catch (e) {
        console.warn("Token refresh failed on login page:", e);
      }
      const user = await AuthService.getCurrentUser();

      // ✅ If any auth activity detected, show loading
      if (isLoginRedirect || hasAuthParams) {
        setRedirectLoginPage(true);
        // Don't clear localStorage - we're in auth flow
        return;
      }

      // ✅ Only clear localStorage if no auth activity
      console.log("Clearing localStorage - no auth activity detected");
      window.localStorage.clear();
    };

    checkAuth();
  }, []);
  const [redirectLoginPage, setRedirectLoginPage] = React.useState(false);
  const dispatch = React.useContext(AuthDispatchContext);
  const redirectToLogin = (type) => {
    if (process.env.REACT_APP_DEVELOPMENT_MODE === "enabled") {
      window.location.replace("/oauth2/login-redirect");
    } else {
      setRedirectLoginPage(true);
      window.localStorage.setItem("LOGIN_TYPE", type);
      fetch(`${process.env.REACT_APP_LOGIN_URL_DOMAIN}/loginurl/?type=${type}`)
        .then((response) => response.text())
        .then((redirectUrl) => window.location.replace(redirectUrl));
    }
  };

  const signInWithMSID = async () => {
    const TEST_USER_EMAIL = "globaladmin_user@mydomain.com";
    dispatch({ type: SET_CREATE_SESSION_FLAG, isCreatingSession: true });
    try {
      if (process.env.REACT_APP_DEVELOPMENT_MODE === "enabled") {
        const user = await fetchUser(TEST_USER_EMAIL);
        dispatch({ type: SET_USER, user });
      } else {
        window.localStorage.setItem("LOGIN_TYPE", "MS-Email");
        await AuthService.signInWithMSID();
      }
      dispatch({ type: SET_CREATE_SESSION_FLAG, isCreatingSession: false });
    } catch (error) {
      if (error.name === "UserAlreadyAuthenticatedException") {
        console.warn("User already authenticated, signing out...");
      }
      dispatch({ type: SET_ERROR, error });
      dispatch({ type: SET_CREATE_SESSION_FLAG, isCreatingSession: false });
    }
  };

  return (
    <div style={{ position: "relative", height: "100%" }}>
      {!redirectLoginPage && (
        <div className={styles.base}>
          <h2 className={styles.title}>Sign in to EIMP Admin</h2>
          <div className={styles.subTitle}>Choose sign in method</div>
          <div className={styles.loginBoxes}>
            <div
              className={styles.loginBoxContainer}
              onClick={() => signInWithMSID()}
            >
              <div className={styles.loginBox}>
                <img
                  className={styles.loginIcon}
                  src="/identification_card.png"
                  alt="MS-Email login"
                />
                <span className={styles.topText}>Use MS-Email</span>
                <span className={styles.bottomText}>(Enterprise Tenant)</span>
              </div>
            </div>

            <div
              className={styles.loginBoxContainer}
              onClick={() => redirectToLogin("OHCID")}
            >
              <div className={styles.loginBox}>
                <img
                  className={styles.loginIcon}
                  src="/Rectangle.png"
                  alt="OneHealthcare ID login"
                />
                <span className={styles.topText}>Use OneHealthcare ID</span>
                <span className={styles.bottomText}>(Single Tenant)</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {redirectLoginPage && (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <div className={styles.loadingText}>
            Authenticating your session, please wait...
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
